import type { HorasDaApi, ParadaDaApi } from './api';

/**
 * A ARITMÉTICA DA TELA DE OPERAÇÃO, separada para poder ser provada.
 *
 * Ela saiu de dentro do componente pelo mesmo motivo que `consumo.ts` saiu:
 * o projeto não tem como renderizar tela em teste (não há jsdom nem
 * testing-library, e instalá-los é decisão que ninguém pediu), então a conta
 * que importa fica onde um teste alcança.
 *
 * E a conta que importa aqui é UMA:
 *
 *   HORA DE MOTOR NÃO ENTRA NA SOMA DO TURNO.
 *
 * O motor anda *durante* produção, espera e deslocamento — uma escavadeira
 * esperando caminhão está com o motor ligado e em espera ao mesmo tempo. As
 * quatro particionam o turno; o motor mede a máquina. Somar as cinco dá mais
 * horas do que o dia tem, e nenhuma tela reclama: o total fica errado,
 * plausível e calado.
 *
 * Este arquivo já nasceu de uma cicatriz. No servidor, a mesma conta chegou a
 * existir em dois lugares — na rota e na view —, e uma sabotagem que fez a view
 * somar as cinco passou por doze testes, porque os testes liam a rota. Lá a
 * correção foi uma coluna gerada, para a fórmula existir uma vez. Aqui a
 * correção é esta: uma função, um teste.
 */

/** Número que chega da API como texto (`numeric` do Postgres vira string). */
function numero(valor: string | null | undefined): number {
  if (valor === null || valor === undefined) return 0;
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

export interface TotaisDoPeriodo {
  /** Medida da máquina. NÃO soma com `apontadas` — ver o cabeçalho. */
  readonly motor: number;
  /** Produção + espera + deslocamento + manutenção. É o turno repartido. */
  readonly apontadas: number;
  readonly producao: number;
  readonly espera: number;
  readonly deslocamento: number;
  readonly manutencao: number;
  readonly turnos: number;
  readonly naoConferidos: number;
}

export const TOTAIS_VAZIOS: TotaisDoPeriodo = {
  motor: 0,
  apontadas: 0,
  producao: 0,
  espera: 0,
  deslocamento: 0,
  manutencao: 0,
  turnos: 0,
  naoConferidos: 0,
};

/**
 * Os totais do que está na tela.
 *
 * Somados na TELA e não no servidor porque a pergunta é da tela — "o que eu
 * estou vendo soma quanto?" — e a resposta tem de bater linha a linha com a
 * tabela logo abaixo. Um total vindo de outra consulta é um número que a tela
 * não consegue explicar quando alguém discorda dele.
 */
export function totaisDoPeriodo(linhas: readonly HorasDaApi[]): TotaisDoPeriodo {
  return linhas.reduce<TotaisDoPeriodo>(
    (t, h) => ({
      motor: t.motor + numero(h.horasMotor),
      /* DA COLUNA `horasApontadas`, que o banco gera, e NÃO de uma soma
         reescrita aqui. Reescrevê-la seria a terceira cópia da mesma fórmula —
         e foi a segunda que deixou um defeito passar por doze testes. */
      apontadas: t.apontadas + numero(h.horasApontadas),
      producao: t.producao + numero(h.horasProducao),
      espera: t.espera + numero(h.horasEspera),
      deslocamento: t.deslocamento + numero(h.horasDeslocamento),
      manutencao: t.manutencao + numero(h.horasManutencao),
      turnos: t.turnos + numero(h.turnos),
      naoConferidos: t.naoConferidos + numero(h.turnosNaoConferidos),
    }),
    TOTAIS_VAZIOS,
  );
}

/**
 * Quanto do turno virou espera, em porcento.
 *
 * `null` quando não há apontamento: dividir por zero daria `Infinity`, e uma
 * tela que escreve "Infinity%" é pior que uma que não escreve nada. A divisão é
 * pelas HORAS APONTADAS — o turno —, nunca pelas de motor: a pergunta é "quanto
 * do tempo disponível foi espera", e o motor não é tempo disponível.
 */
export function proporcaoDeEspera(totais: TotaisDoPeriodo): number | null {
  if (totais.apontadas <= 0) return null;
  return (totais.espera / totais.apontadas) * 100;
}

/**
 * Horas paradas por causa EVITÁVEL.
 *
 * É a oportunidade que o v6 dá ao módulo, com estas palavras: "reduzir paradas
 * evitáveis". Chuva e frente não liberada pelo cliente entram nas horas
 * paradas e ficam FORA deste número — misturá-las faria a empresa perseguir o
 * que não pode evitar.
 *
 * Parada ainda aberta tem `horasParadas` nulo e não entra: somar `now()` faria
 * o número mudar a cada consulta, e um relatório que não repete o próprio
 * resultado não é relatório. A regra do nulo é a de `somaDasHorasParadas`.
 */
export function horasEvitaveis(paradas: readonly ParadaDaApi[]): number | null {
  return somaDasHorasParadas(paradas.filter((p) => p.evitavel));
}

/**
 * A soma das horas paradas JÁ ENCERRADAS — o rodapé da coluna e o card.
 *
 * `null` quando há causa na lista e nenhuma encerrou: todas vêm com
 * `horasParadas` nulo, e somá-las como zero escrevia "0,0 h" logo abaixo de uma
 * linha que diz "ainda aberta" — afirmava que a causa não custou nada, o
 * contrário do que está acontecendo. Lista vazia soma zero: sem parada, zero é
 * verdade.
 */
export function somaDasHorasParadas(paradas: readonly ParadaDaApi[]): number | null {
  const encerradas = paradas.filter((p) => p.horasParadas !== null);
  if (paradas.length > 0 && encerradas.length === 0) return null;
  return encerradas.reduce((t, p) => t + numero(p.horasParadas), 0);
}

/** Quantas paradas continuam abertas — a informação que sai da soma acima. */
export function paradasEmAberto(paradas: readonly ParadaDaApi[]): number {
  return paradas.reduce((t, p) => t + numero(p.emAberto), 0);
}

/** As abertas de causa evitável: o que ficou fora de `horasEvitaveis`. */
export function evitaveisEmAberto(paradas: readonly ParadaDaApi[]): number {
  return paradasEmAberto(paradas.filter((p) => p.evitavel));
}

/**
 * A soma das quatro naturezas do formulário, enquanto se digita.
 *
 * Recebe TEXTO porque é isso que o campo tem, e campo vazio vale zero — quem
 * não trabalhou em deslocamento deixa em branco, e exigir "0" seria exigir uma
 * afirmação para dizer nada.
 *
 * O motor NÃO é parâmetro desta função, e a ausência é o ponto: não há como
 * incluí-lo por descuido.
 */
export function somaDoTurno(campos: {
  readonly producao: string;
  readonly espera: string;
  readonly deslocamento: string;
  readonly manutencao: string;
}): number {
  const valor = (texto: string) => {
    const limpo = texto.trim().replace(',', '.');
    if (limpo === '') return 0;
    const n = Number(limpo);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };
  return (
    valor(campos.producao) +
    valor(campos.espera) +
    valor(campos.deslocamento) +
    valor(campos.manutencao)
  );
}

/** O teto que o banco também cobra (`check`), repetido para a recusa vir com frase. */
export const HORAS_MAXIMAS_DO_TURNO = 24;

export interface DisponibilidadeDaFrota {
  /** Janela programada somada das máquinas que aparecem na tabela. */
  readonly programadas: number;
  /** (programadas − manutenção) ÷ programadas. Entre 0 e 1. */
  readonly disponibilidade: number;
  /** Janela sem nenhuma hora apontada: o furo do registro. */
  readonly semApontamento: number;
}

/**
 * A disponibilidade da frota na tela (decisão 19, turno da empresa).
 *
 * Nulo quando não há janela: sem turno cadastrado não existe denominador, e
 * inventar um seria voltar ao "20 horas num turno de 8" que o turno veio
 * resolver. A frota é a da tabela — as máquinas que apontaram no período.
 */
export function disponibilidadeDaFrota(linhas: readonly HorasDaApi[]): DisponibilidadeDaFrota | null {
  let programadas = 0;
  let manutencao = 0;
  let semApontamento = 0;
  for (const h of linhas) {
    if (h.horasProgramadas === null || h.horasProgramadas === undefined) return null;
    programadas += h.horasProgramadas;
    manutencao += numero(h.horasManutencao);
    semApontamento += h.horasSemApontamento ?? 0;
  }
  if (programadas <= 0) return null;
  return {
    programadas,
    disponibilidade: Math.max(0, (programadas - manutencao) / programadas),
    semApontamento,
  };
}
